import { useEffect, useRef, useState } from 'react';

interface UseLazyImageProps {
  src: string;
  alt: string;
  threshold?: number;
  rootMargin?: string;
}

export const useLazyImage = ({ 
  src, 
  alt, 
  threshold = 0.1, 
  rootMargin = '50px' 
}: UseLazyImageProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold, rootMargin }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, [threshold, rootMargin]);

  const handleLoad = () => {
    setIsLoaded(true);
    if (imgRef.current) {
      imgRef.current.classList.add('loaded');
    }
  };

  return {
    imgRef,
    src: isInView ? src : undefined,
    alt,
    loading: 'lazy' as const,
    onLoad: handleLoad,
    isLoaded,
    isInView,
    className: `transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`
  };
};